import { client } from "@/hera-api/client/client.gen";
import { isPhotoAnswer } from "@/lib/photo-answer";

/** File token string, or Photo-style object with optional capture metadata. */
export type AnswerFileValue =
  | string
  | {
      token: string;
      content_type?: string | null;
      latitude?: number | null;
      longitude?: number | null;
      accuracy_meters?: number | null;
      captured_at?: string | null;
      timezone?: string | null;
      [key: string]: unknown;
    };

export type AnswerFileMetadata = {
  latitude?: number | null;
  longitude?: number | null;
  accuracy_meters?: number | null;
  captured_at?: string | null;
  timezone?: string | null;
  content_type?: string | null;
  captured_at_corrected?: string | null;
  position_timestamp?: string | null;
  clock_offset_ms?: number | null;
  time_sync_age_ms?: number | null;
  server_received_at?: string | null;
  server_skew_ms?: number | null;
  distance_meters_from_property?: number | null;
};

const IMAGE_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "bmp",
  "heic",
  "heif",
  "tif",
  "tiff",
]);

export function isAnswerFileAnswerType(
  answerType: string | undefined,
): boolean {
  if (!answerType) return false;
  return answerType === "document" || answerType === "photo";
}

/** File tokens for a document answer: one string, or a list when multiple are allowed. */
export function documentAnswerTokens(answer: unknown): string[] {
  if (typeof answer === "string" && answer.trim()) {
    return [answer.trim()];
  }
  if (!Array.isArray(answer)) return [];
  return answer.filter(
    (item): item is string =>
      typeof item === "string" && item.trim().length > 0,
  );
}

export function extractAnswerFileToken(answer: unknown): string | null {
  if (typeof answer === "string" && answer.trim()) {
    return answer.trim();
  }
  if (
    typeof answer === "object" &&
    answer !== null &&
    "token" in answer &&
    typeof (answer as { token?: unknown }).token === "string"
  ) {
    const token = (answer as { token: string }).token.trim();
    return token || null;
  }
  return null;
}

export function extractAnswerFileMetadata(
  answer: unknown,
): AnswerFileMetadata | null {
  if (isPhotoAnswer(answer)) {
    return {
      latitude: answer.latitude,
      longitude: answer.longitude,
      accuracy_meters: answer.accuracy_meters,
      captured_at: answer.captured_at,
      timezone: answer.timezone,
      content_type: answer.content_type ?? "image/jpeg",
      captured_at_corrected: answer.captured_at_corrected ?? null,
      position_timestamp: answer.position_timestamp ?? null,
      clock_offset_ms: answer.clock_offset_ms ?? null,
      time_sync_age_ms: answer.time_sync_age_ms ?? null,
      server_received_at: answer.server_received_at ?? null,
      server_skew_ms: answer.server_skew_ms ?? null,
      distance_meters_from_property:
        answer.distance_meters_from_property ?? null,
    };
  }
  if (typeof answer !== "object" || answer === null) return null;
  const record = answer as Record<string, unknown>;
  if (typeof record.token !== "string") return null;

  const meta: AnswerFileMetadata = {};
  if ("latitude" in record) meta.latitude = record.latitude as number | null;
  if ("longitude" in record) meta.longitude = record.longitude as number | null;
  if ("accuracy_meters" in record)
    meta.accuracy_meters = record.accuracy_meters as number | null;
  if ("captured_at" in record)
    meta.captured_at = record.captured_at as string | null;
  if ("timezone" in record) meta.timezone = record.timezone as string | null;
  if ("content_type" in record)
    meta.content_type = record.content_type as string | null;
  if ("captured_at_corrected" in record)
    meta.captured_at_corrected = record.captured_at_corrected as string | null;
  if ("position_timestamp" in record)
    meta.position_timestamp = record.position_timestamp as string | null;
  if ("clock_offset_ms" in record)
    meta.clock_offset_ms = record.clock_offset_ms as number | null;
  if ("time_sync_age_ms" in record)
    meta.time_sync_age_ms = record.time_sync_age_ms as number | null;
  if ("server_received_at" in record)
    meta.server_received_at = record.server_received_at as string | null;
  if ("server_skew_ms" in record)
    meta.server_skew_ms = record.server_skew_ms as number | null;
  if ("distance_meters_from_property" in record)
    meta.distance_meters_from_property = record.distance_meters_from_property as
      | number
      | null;

  return Object.keys(meta).length > 0 ? meta : null;
}

export function guessIsImageFromToken(token: string): boolean {
  const ext = token.includes(".")
    ? token.split(".").pop()?.toLowerCase()
    : undefined;
  return !!ext && IMAGE_EXTENSIONS.has(ext);
}

export function isImageMimeType(mime: string | null | undefined): boolean {
  return !!mime && mime.toLowerCase().startsWith("image/");
}

/**
 * Fetch an answer file with the hey-api client auth token (operator / government /
 * registrant portals). Returns an object URL the caller must revoke.
 */
export async function fetchAnswerFileBlobUrl(token: string): Promise<{
  blobUrl: string;
  contentType: string | null;
}> {
  const baseUrl = (client.getConfig().baseUrl || "").replace(/\/$/, "");
  const auth = client.getConfig().auth;
  const doFetch = client.getConfig().fetch ?? globalThis.fetch.bind(globalThis);
  const response = await doFetch(
    new Request(`${baseUrl}/shared/answer_files/${token}`, {
      headers: { Authorization: `Bearer ${auth}` },
    }),
  );
  if (!response.ok) {
    throw new Error(`Failed to load file (${response.status})`);
  }
  const blob = await response.blob();
  return {
    blobUrl: URL.createObjectURL(blob),
    contentType: blob.type || response.headers.get("content-type"),
  };
}
